import { useSettings } from '../SettingsContext';
import culturalPatternBg from '../../assets/8f320715a0789cd66ecbac8ed3c33f41f7bf02e3.png';
import darkModeBg from '../../assets/3aa95779ad637ef1d40ddc06f12ff02444cc906d.png';

interface ParallaxBackgroundProps {
    /**
     * @deprecated The background no longer moves with scroll. Still accepted so
     * the ~40 existing call sites keep compiling; it has no effect.
     */
    speed?: number;
    /**
     * @deprecated No longer used. The layer does not track a scroll container.
     */
    targetId?: string;
}

/**
 * The textured pattern behind a screen's content. Static: it stays put while
 * the content scrolls over it.
 *
 * It used to translate downward by a fraction of scrollTop, which is what made
 * the background look like it was peeling away from the top of the screen. The
 * element sat at top: 0, so shifting it down opened a gap above it and exposed
 * the flat background colour underneath.
 *
 * Two things hid how broken that was. The component also rendered a pair of
 * correctly-fixed layers, but they carried z-[-1] while neither the screen root
 * nor .app-container establishes a stacking context — so they painted behind the
 * root's own opaque background and were never visible at all. And a single ref
 * was attached to both the light and dark moving layers, so it ended up pointing
 * at whichever committed last (the dark one). Only the dark layer was ever
 * transformed, which is why the drift showed in dark mode and not in light.
 *
 * Positioned absolute rather than fixed: every call site places this inside a
 * viewport-height, non-scrolling screen root, so inset-0 already pins it to the
 * viewport, and it avoids the stacking-context trap that made the old fixed
 * layers invisible.
 *
 * Renders nothing when patternIntensity is 'off'.
 */
export function ParallaxBackground(_props: ParallaxBackgroundProps) {
    const { patternIntensity } = useSettings();

    if (patternIntensity === 'off') return null;

    // Shared between the two themed layers so they cannot drift apart.
    const layer = {
        backgroundSize: '100% auto', // full width, auto height preserves aspect
        backgroundPosition: 'top center',
        backgroundRepeat: 'repeat-y' as const, // tiles seamlessly down the screen
        opacity: patternIntensity === 'subtle' ? 0.5 : 1,
    };

    return (
        <>
            {/* Light */}
            <div
                className="absolute inset-0 pointer-events-none z-0 dark:hidden"
                style={{ ...layer, backgroundImage: `url(${culturalPatternBg})` }}
            />
            {/* Dark */}
            <div
                className="absolute inset-0 pointer-events-none z-0 hidden dark:block"
                style={{ ...layer, backgroundImage: `url(${darkModeBg})` }}
            />
        </>
    );
}
